import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Printer, X, Activity, ShieldCheck } from 'lucide-react';
import { formatDateTime } from '@/utils/dateTime';

export const PrescriptionPrintModal = ({ open, onOpenChange, consultation, patient, doctor, clinicName, hospitalName }) => {
  const printRef = React.useRef();

  React.useEffect(() => {
    if (open) {
      document.body.classList.add('modal-printing');
    } else {
      document.body.classList.remove('modal-printing');
    }
    return () => {
      document.body.classList.remove('modal-printing');
    };
  }, [open]);

  if (!consultation && !patient) return null;

  const handlePrint = () => {
    window.print();
  };

  const currentDate = consultation?.date || new Date().toISOString();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 border-0 bg-slate-100">
        <DialogHeader className="p-4 bg-slate-900 text-white rounded-t-xl flex flex-row items-center justify-between">
          <div>
            <DialogTitle className="text-base font-bold text-white flex items-center gap-2">
              <Printer className="h-4 w-4 text-teal-400" />
              OPD Prescription & Clinical Summary
            </DialogTitle>
            <p className="text-[11px] text-slate-400">Official signed clinical prescription sheet</p>
          </div>
        </DialogHeader>

        {/* Printable Paper Area */}
        <div
          ref={printRef}
          id="printable-prescription"
          className="p-8 bg-white mx-auto my-4 max-w-2xl w-full rounded-xl shadow-md border border-slate-200 text-slate-800 space-y-6 print:shadow-none print:border-none print:m-0 print:p-6 print:max-w-full"
        >
          {/* Hospital Letterhead */}
          <div className="flex items-start justify-between border-b-2 border-teal-700 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-teal-700 text-white flex items-center justify-center font-bold text-sm">
                  PF
                </div>
                <h2 className="text-xl font-heading font-extrabold text-slate-950 uppercase tracking-tight">
                  {hospitalName || 'Apollo Memorial Multi-Specialty Hospital'}
                </h2>
              </div>
              <p className="text-xs text-slate-600">
                Bannerghatta Main Road, Bengaluru, Karnataka • Reg: CEA/KA/2022/4928
              </p>
              <p className="text-[11px] text-slate-500">
                Department: <span className="font-semibold text-slate-800">{clinicName || 'General Medicine OPD'}</span> • Emergency: +91 80 2345 6789
              </p>
            </div>
            <div className="text-right space-y-0.5">
              <Badge variant="outline" className="bg-teal-50 text-teal-800 border-teal-200 text-[10px] font-bold">
                OPD PRESCRIPTION
              </Badge>
              <p className="text-[11px] text-slate-500">
                Date: <span className="font-bold text-slate-800">{formatDateTime(currentDate)}</span>
              </p>
            </div>
          </div>

          {/* Patient Demographics & Vitals Bar */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <span className="text-slate-400 text-[10px] uppercase font-semibold">Patient Name</span>
              <p className="font-bold text-slate-900">{patient?.firstName} {patient?.lastName}</p>
            </div>
            <div>
              <span className="text-slate-400 text-[10px] uppercase font-semibold">Age / Gender</span>
              <p className="font-semibold text-slate-800">{patient?.age || '-'} Yrs / {patient?.gender || '-'}</p>
            </div>
            <div>
              <span className="text-slate-400 text-[10px] uppercase font-semibold">UHID / Patient ID</span>
              <p className="font-mono font-bold text-teal-800">{patient?.uhid || patient?.id || 'P-10024'}</p>
            </div>
            <div>
              <span className="text-slate-400 text-[10px] uppercase font-semibold">Consulting Doctor</span>
              <p className="font-bold text-slate-900">{doctor?.name || 'Dr. Rajesh Kumar'}</p>
            </div>

            {/* Vitals */}
            {consultation?.vitals && (
              <div className="col-span-2 sm:col-span-4 pt-2 mt-1 border-t border-slate-200 flex flex-wrap gap-4 text-[11px]">
                <span className="text-slate-500 font-semibold">Vitals:</span>
                {consultation.vitals.bp && <span>BP: <strong>{consultation.vitals.bp} mmHg</strong></span>}
                {consultation.vitals.pulse && <span>Pulse: <strong>{consultation.vitals.pulse} bpm</strong></span>}
                {consultation.vitals.temp && <span>Temp: <strong>{consultation.vitals.temp} °F</strong></span>}
                {consultation.vitals.weight && <span>Weight: <strong>{consultation.vitals.weight} kg</strong></span>}
                {consultation.vitals.spo2 && <span>SpO2: <strong>{consultation.vitals.spo2}%</strong></span>}
              </div>
            )}
          </div>

          {/* Clinical Diagnosis & Notes */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider text-teal-800">
              Clinical Assessment & Diagnosis
            </h4>
            <div className="p-3 bg-white border border-slate-200 rounded-lg text-xs space-y-1">
              <p className="font-bold text-slate-900 text-sm">
                {consultation?.diagnosis || 'Essential (Primary) Hypertension with Mild Bronchitis'}
              </p>
              {consultation?.notes && (
                <p className="text-slate-600 text-xs italic">{consultation.notes}</p>
              )}
            </div>
          </div>

          {/* Rx Medications Table */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-serif font-black text-xl text-teal-800 italic">℞</span>
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Prescribed Medications (Rx)
              </h4>
            </div>

            <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-slate-100/80 text-slate-700 font-bold border-b border-slate-200">
                <tr>
                  <th className="p-2 text-left w-8">#</th>
                  <th className="p-2 text-left">Medicine & Strength</th>
                  <th className="p-2 text-left">Dosage</th>
                  <th className="p-2 text-left">Frequency</th>
                  <th className="p-2 text-left">Duration</th>
                  <th className="p-2 text-left">Instructions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(consultation?.prescriptions || [
                  { medicine: 'Tab. Telmisartan', dosage: '40mg', frequency: '1-0-0', duration: '30 Days', instructions: 'After breakfast' },
                  { medicine: 'Tab. Amlodipine', dosage: '5mg', frequency: '0-0-1', duration: '30 Days', instructions: 'Before bedtime' },
                  { medicine: 'Cap. Amoxicillin', dosage: '500mg', frequency: '1-0-1', duration: '5 Days', instructions: 'After food' },
                ]).map((rx, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="p-2 text-slate-400 font-semibold">{idx + 1}</td>
                    <td className="p-2 font-bold text-slate-900">{rx.medicine || rx.name}</td>
                    <td className="p-2 text-slate-700">{rx.dosage || '-'}</td>
                    <td className="p-2 font-mono font-bold text-teal-700">{rx.frequency || '-'}</td>
                    <td className="p-2 text-slate-700">{rx.duration || '-'}</td>
                    <td className="p-2 text-slate-500 italic">{rx.instructions || 'After meals'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Advice & Follow-up */}
          <div className="grid grid-cols-2 gap-4 pt-2 text-xs">
            <div className="p-3 bg-amber-50/60 border border-amber-200 rounded-lg">
              <span className="font-bold text-amber-900 uppercase text-[10px]">General Advice & Diet</span>
              <p className="text-amber-800 text-[11px] mt-0.5">
                Low sodium diet. Avoid strenuous workouts. Plenty of hydration.
              </p>
            </div>
            <div className="p-3 bg-teal-50/60 border border-teal-200 rounded-lg">
              <span className="font-bold text-teal-900 uppercase text-[10px]">Recommended Follow-up</span>
              <p className="text-teal-800 text-[11px] mt-0.5">
                Review in OPD after 14 days or immediately if blood pressure exceeds 160/100.
              </p>
            </div>
          </div>

          {/* Doctor Signature Block */}
          <div className="pt-8 flex items-end justify-between border-t border-slate-200">
            <div className="text-[10px] text-slate-400 space-y-0.5">
              <p>Generated electronically by PulseFlow Health System</p>
              <p>Security Hash: SHA256-VLD-{(patient?.id || 'P1').slice(0, 8)}</p>
            </div>
            <div className="text-right space-y-1">
              <div className="w-36 border-b border-slate-400 ml-auto pb-1">
                <span className="font-serif italic text-xs text-teal-800 font-bold">Dr. Rajesh Kumar</span>
              </div>
              <p className="font-bold text-xs text-slate-900">{doctor?.name || 'Dr. Rajesh Kumar'}</p>
              <p className="text-[10px] text-slate-500">{doctor?.qualification || 'MBBS, MD (Cardiology)'}</p>
              <p className="text-[10px] text-slate-500">MCI Reg: {doctor?.registrationNo || 'MCI/2018/84729'}</p>
            </div>
          </div>
        </div>

        {/* Modal Action Buttons */}
        <DialogFooter className="p-4 bg-white border-t border-slate-200 flex sm:justify-between">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-1.5" />
            Close Preview
          </Button>
          <Button size="sm" onClick={handlePrint} className="bg-teal-600 hover:bg-teal-700 text-white font-bold">
            <Printer className="h-4 w-4 mr-1.5" />
            Print Prescription
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionPrintModal;

